/*
 * (c) 2023 Amazon Web Services, Inc. or its affiliates. All Rights Reserved.
 * 
 * This AWS Content is provided subject to the terms of the AWS Customer Agreement available at http://aws.amazon.com/agreement or other written agreement between Customer and either Amazon Web Services, Inc. or Amazon Web Services EMEA SARL or both.
 */
import React from 'react';
import './Game.css';
import LoadingScreen from './LoadingScreen';
import UploadModal from './UploadModal';
import BugReportModal from './BugReportModal';

import {
    Badge,
    Button,
    ButtonDropdown,
    ButtonDropdownProps,
    Container,
    Box,
    //CopyToClipboard,
    Header,
    SpaceBetween
} from "@cloudscape-design/components";

import * as gameliftstreamssdk from "../gameliftstreamssdk/gameliftstreams-1.0.0.mjs"
import { CredentialManager } from '../utils/CredentialManager';
import { testCredentialManager } from '../utils/test-credentials';

import { ApiError, post, get } from 'aws-amplify/api';
import { fetchAuthSession } from 'aws-amplify/auth';


const defaultLoadMessage = "Initializing stream. This may take a few minutes.";
const connectingLoadMessage = "Establishing connection. Almost there!";
const reconnectLoadMessage = "Attempting to reconnect. This shouldn't take long.";

interface GameProps {
    gameName: string,
    gameDescription: string,
    appId: string,
    sgId: string,
    streamClass: string,
    region: string,
    remainingStreams: number,
    setError: (error: string) => void,
    refreshList: () => void,
    setSuccess: (success: string) => void,
    isRecommended: boolean,
    isActiveStream: boolean,
    hasActiveStream: boolean,
    setActiveStream: (sgId: string | null) => void,
    onReconnectAvailable: (sgId: string | null) => void,
}

interface AuthContextProps {
    auth: any
}

enum StreamState {
    STOPPED = 1,
    LOADING,
    RUNNING,
    ERROR
}

interface GameState {
    status: StreamState,
    pointerLocked: boolean,
    inputEnabled: boolean,
    error: string,    
    success:string,
    micro: boolean,
    arn: string,
    region: string,
    showLoading: boolean,
    loadingMessage: string,
    showUploadModal: boolean,
    showBugReportModal: boolean,
    isSubmittingBugReport: boolean,
    checkingReconnect: boolean,
    canReconnect: boolean,
}

class Game extends React.Component<GameProps & AuthContextProps, GameState> {
    gameLiftStreams?: typeof gameliftstreamssdk.GameLiftStreams ; 
    private pollInterval: NodeJS.Timeout | null = null;
    private credentialManager: CredentialManager;

    constructor(props: GameProps & AuthContextProps) {
        super(props);
        this.credentialManager = new CredentialManager();
        this.state = {
            status: StreamState.STOPPED,
            pointerLocked: false,
            error: "",
            success: "",
            micro: false,
            arn: "",
            region: "",
            inputEnabled: false,
            showLoading: false,
            loadingMessage: defaultLoadMessage,
            showUploadModal: false,
            showBugReportModal: false,
            isSubmittingBugReport: false,
            checkingReconnect: false,
            canReconnect: false,
        };

        this.dataChannelProcessMessage = this.dataChannelProcessMessage.bind(this);

        gameliftstreamssdk.setLogLevel('debug');
        this.createStreamSession = this.createStreamSession.bind(this);
        this.enableFullScreen = this.enableFullScreen.bind(this);
        this.closeConnection = this.closeConnection.bind(this);
        this.enableInput = this.enableInput.bind(this);
        this.enablePointerLock = this.enablePointerLock.bind(this);
        this.enableMic = this.enableMic.bind(this);
        this.onButtonDropdownItemClick = this.onButtonDropdownItemClick.bind(this);
        this.startPolling = this.startPolling.bind(this);
        this.stopPolling = this.stopPolling.bind(this);
        this.handleWindowFocus = this.handleWindowFocus.bind(this);
    }

    private getVideoElement(): HTMLVideoElement {
        return document.getElementById(`StreamVideoElement${this.props.gameName}${this.props.sgId}`) as HTMLVideoElement;
    }

    private getAudioElement() : HTMLAudioElement {
        return document.getElementById(`StreamAudioElement${this.props.gameName}${this.props.sgId}`) as HTMLAudioElement;
    }

    async componentDidMount() {
        try {
            // Test credential manager on startup (optional)
            if (this.props.auth.isAuthenticated) {
                console.log('🧪 Testing Amplify Auth credentials...');
                await testCredentialManager();
            }
            
            await this.createGameLiftStream();
            this.startPolling();
            await this.checkReconnect();
            
            // Add window focus listener to handle input loss on alt+tab
            window.addEventListener('focus', this.handleWindowFocus);
        } catch (error) {
            console.error('Error in componentDidMount:', error);
            this.props.setError('Failed to initialize stream');
            this.setState({
                status: StreamState.ERROR
            });
        }
    }

    componentWillUnmount() {
        console.log("Unmount called.");
        this.stopPolling();
        // No auth management to stop
        if (this.gameLiftStreams) {
            this.gameLiftStreams.close();
        }
        // Remove event listeners
        window.removeEventListener('focus', this.handleWindowFocus);
    }

    startPolling = () => {
        // Clear any existing interval
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
        }

        // Set new interval - 180000ms = 3 minutes
        this.pollInterval = setInterval(() => {
            // Your code to run every 5 minutes
            // For example:
            if (this.state.status === StreamState.STOPPED) {
                this.props.refreshList();
            }
            if(this.state.canReconnect)
            {
                this.checkReconnect();
            }
        }, 180000);
    }

    stopPolling = () => {
        if (this.pollInterval) {
            clearInterval(this.pollInterval);
            this.pollInterval = null;
        }
    }





    componentDidUpdate(prevProps: GameProps, prevState: GameState) {
        if (prevState.status !== this.state.status) {
            if (this.state.status === StreamState.STOPPED) {
                this.props.refreshList();
                this.startPolling(); // Restart polling when status changes
            }
        }
    }

    createGameLiftStream()
    {
        const videoElement = this.getVideoElement();
        const audioElement = this.getAudioElement();

        this.gameLiftStreams = new gameliftstreamssdk.GameLiftStreams({
            videoElement: videoElement,
            audioElement: audioElement,
            inputConfiguration: {
                autoMouse: true,
                autoKeyboard: true,
                autoGamepad: true,
                hapticFeedback: true,
                setCursor: 'visibility',
                autoPointerLock: 'fullscreen',
            },
            clientConnection:
            {
                applicationMessage: this.dataChannelProcessMessage,
            }
        })
    }

    async createStreamSession() {
        this.setState({ status: StreamState.LOADING, showLoading: true, loadingMessage: defaultLoadMessage });
        this.props.setActiveStream(this.props.sgId);
        const signalRequest = await this.gameLiftStreams?.generateSignalRequest();
        try {
            // Initial stream session creation
            const token = await this.ensureValidToken();
            const restOperation = post({
                apiName: "gamecast-api",
                path: "/start",
                options: {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    },
                    body: {
                        AppIdentifier: this.props.appId,
                        SGIdentifier: this.props.sgId,
                        UserId: "DefaultUser",
                        SignalRequest: signalRequest ?? "",
                        Regions: [this.props.region]
                    }
                }
            });

            const { body } = await restOperation.response;
            const bodyText = await body.text();
            const data = JSON.parse(bodyText);
            console.log("Initial response:", data);
            const arn = data.arn;
            console.log("Initial Arn: ", arn);

            // If status is ACTIVATING, poll using GetStreamSession
            if (data.status === 'ACTIVATING') {
                let attempts = 0;
                const maxAttempts = 500;
                console.log("Polling Arn: ", arn);

                while (attempts < maxAttempts) {
                    const pollToken = await this.ensureValidToken();
                    const pollOperation = get({
                        apiName: "gamecast-api",
                        path: `/session/${encodeURIComponent(this.props.sgId)}/${encodeURIComponent(arn)}`,
                        options: {
                            headers: {
                                Authorization: `Bearer ${pollToken}`
                            }
                        }
                    });

                    console.log("Poll request: ", pollOperation);
                    const pollResponse = await pollOperation.response;
                    const pollData = JSON.parse(await pollResponse.body.text());
                    console.log("Poll response:", pollData);

                    if (pollData.status === 'ACTIVE') {
                        this.setState({ loadingMessage: connectingLoadMessage });
                        // Update data with the active session data
                        data.status = pollData.status;
                        data.signalResponse = pollData.signalResponse;
                        break;
                    } else if (pollData.status === 'ERROR') {
                        this.setState({
                            status: StreamState.ERROR,
                            arn: data.arn,
                            region: data.region,
                            inputEnabled: true
                        });
                        throw new Error('Stream session entered error state');
                    }
                    else if (pollData.status === 'TERMINATED') {
                        this.setState({ loadingMessage: "The session has unexpectedly terminated." });
                        this.setState({
                            status: StreamState.ERROR,
                            arn: data.arn,
                            region: data.region,
                            inputEnabled: true
                        });
                        throw new Error('Stream session was terminated unexpectedly.');
                    }

                    await new Promise(resolve => setTimeout(resolve, 2000));
                    attempts++;
                }

                if (attempts >= maxAttempts) {
                    throw new Error('Timeout waiting for stream session to activate');
                }
            }

            // Process the signal response once we have it
            await this.gameLiftStreams?.processSignalResponse(data.signalResponse);
            this.gameLiftStreams?.attachInput();

            this.setState({
                status: StreamState.RUNNING,
                arn: data.arn,
                region: data.region,
                inputEnabled: true
            }, () => {
                this.saveStreamState(this.props.sgId, this.state.arn, this.props.region);
            });
        } catch (e) {
            console.log(e);
            this.props.setActiveStream(null);
            if (e instanceof ApiError) {
                if (e.response) {
                    const { statusCode, body } = e.response;
                    const data = JSON.parse(body ?? "");
                    console.error(`Received ${statusCode} error response with payload: ${body}`);
                    this.props.setError(data.message);
                    this.setState({ status: StreamState.ERROR });
                }
            }
            else
            {
                const errorMessage = e instanceof Error
                    ? `${e.name}: ${e.message}`
                    : 'An unknown error occurred';

                this.props.setError(errorMessage);
                this.setState({ status: StreamState.ERROR });
            }
        }
    }

    async reconnectStream(streamGroupIdentifier: string, streamSessionIdentifier: string, streamRegion: string) {
        this.setState({ status: StreamState.LOADING, showLoading: true, loadingMessage: reconnectLoadMessage });
        this.props.setActiveStream(this.props.sgId);
        console.log("Attempting to reconnect: ", streamGroupIdentifier, streamSessionIdentifier, streamRegion);
        const signalRequest = await this.gameLiftStreams?.generateSignalRequest();
        console.log("Got signal request");
        try {
            console.log("Sending reconnect request");
    
            const token = await this.ensureValidToken();
            const restOperation = post({
                apiName: "gamecast-api",
                path: `/reconnect/`,
                options: {
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: {
                        StreamSessionIdentifier: streamSessionIdentifier,
                        SGIdentifier: streamGroupIdentifier,
                        SignalRequest: signalRequest
                    }
                }
            });
            const { body } = await restOperation.response;
            const data = JSON.parse(await body.text());
            console.log("Initial response:", data);
    
            if(data.signalResponse) {
                // Process the signal response once we have it
                await this.gameLiftStreams?.processSignalResponse(data.signalResponse);
                this.gameLiftStreams?.attachInput();
    
                this.setState({
                    status: StreamState.RUNNING,
                    arn: streamSessionIdentifier,
                    region: streamRegion,
                    inputEnabled: true,
                    showLoading: false
                }, () => {
                    this.saveStreamState(this.props.sgId, this.state.arn, this.props.region);
                });
            }
    
        } catch (e) {
            this.clearLocalStorage();
            this.props.setActiveStream(null);
            console.log(e);
            if (e instanceof ApiError) {
                if (e.response) {
                    const { statusCode, body } = e.response;
                    const data = JSON.parse(body ?? "");
                    console.error(`Received ${statusCode} error response with payload: ${body}`);

                    // Check if the error is about stream session status
                    if (statusCode === 500 && data.message.includes('is not in ACTIVE or PENDING_CLIENT_RECONNECTION status')) {
                        this.props.setError(data.message);
                        this.setState({
                            status: StreamState.STOPPED,
                            arn: "",
                            region: "",
                            inputEnabled: false
                        });
                    } else {
                        this.props.setError(data.message);
                        this.setState({ status: StreamState.ERROR });
                    }
                }
            }
        }
    }    

    openInNewTab = (url: string) => {
        const features = 'noopener,noreferrer';
        window.open(url, '_blank', features);
    };

    handleJsonUpload = (jsonData: any) => {
        // Handle the JSON data here
        const message = JSON.stringify({
            action: "lightFileUpload",
            config: jsonData
        });
        console.log('Sending message JSON data:', message);        

        this.dataChannelSendMessage(message);
        // Close the modal
        this.setState({ showUploadModal: false });
    };

    dataChannelSendMessage = (message: string) => {
        console.log("[DataChannel] Attempting to send message:", message);
        if (!this.state.inputEnabled) {
            console.warn("[DataChannel] Cannot send message. You must enable input to allow communication through the data channel.");
            return;
        }
        const textEncoder = new TextEncoder();
        if (this.gameLiftStreams) {
            const result: boolean = this.gameLiftStreams.sendApplicationMessage(textEncoder.encode(message));
            if (result === false) {
                console.error("[DataChannel] Message failed to send.");
            } else {
                console.log("[DataChannel] Message sent successfully:", message);
            }
        } else {
            console.error("[DataChannel] GameLift Streams not initialized.");
        }
    };

    private async ensureValidToken(): Promise<string> {
        try {
            const session = await fetchAuthSession();
            const idToken = session.tokens?.idToken?.toString();
            
            if (!idToken) {
                throw new Error('No valid tokens available');
            }
            
            return idToken;
        } catch (error) {
            console.error('Failed to get valid token:', error);
            throw new Error('Authentication failed');
        }
    }







    private async getAWSCredentials() {
        try {
            console.log('[Auth] Getting AWS credentials via Amplify Auth');
            return await this.credentialManager.getCredentials();
        } catch (error) {
            console.error('Failed to get AWS credentials:', error);
            return {
                accessKeyId: null,
                secretAccessKey: null,
                sessionToken: null,
                expiration: null,
                region: this.props.region
            };
        }
    }

    // Processes a message received from the GameCast Data Channel Demo
    dataChannelProcessMessage = (message: Uint8Array) => {
        const textDecoder = new TextDecoder();
        const strMessage: string = textDecoder.decode(message);
        console.log('[DataChannel] Received message from application: ' + strMessage);
        // Remove control characters (0x00-0x1F except tab, newline, carriage return which JSON allows when escaped)
        // eslint-disable-next-line no-control-regex
        const sanitizedMessage = strMessage.replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F]/g, '');
        const obj = JSON.parse(sanitizedMessage);
        const action = obj.action;
        console.log("[DataChannel] Received message: " + action);

        switch (action) {
            // Message received form: {"action":"copytext","text":"<copied text>"}
            case "copytext":
                console.log("[DataChannel] Copying.");
                if (obj.text) {
                    navigator.clipboard.writeText(obj.text).then(
                        () => {
                            console.log("[DataChannel] Successfully wrote to clipboard: " + obj.text);
                        },
                        () => {
                            console.error("[DataChannel] Failed to write to clipboard: " + obj.text);
                        },
                    );
                }
                break;
            case "download":
                console.log("[DataChannel] Downloading.");
                if (obj.text) {
                    this.openInNewTab(obj.text);
                }
                break;
            // Message received form: {"action":"requestpaste"}
            // Message send form: {"action":"pasteresponse","text":"**"} 
            case "requestpaste":
                console.log("[DataChannel] Sending clipboard paste to Stream.");
                navigator.clipboard.readText().then(
                    (clipText: string) => (
                        this.dataChannelSendMessage(JSON.stringify({
                            action: "pasteresponse",
                            text: clipText
                        }))
                    )
                );
                break;
            // Message received form: {"action":"openurl", "url":"https://example.com"}
            case "openurl":
                if (obj.url) {
                    const url: string = obj.url;
                    window.open(url);
                }
                break;
            case "requestCredentials":
                this.setState({showLoading: false });
                console.log("[DataChannel] Sending Credentials to Stream.");
                fetchAuthSession().then(session => {
                    const credentialsMessage = JSON.stringify({
                        action: "credentialsResponse",
                        IdToken: session.tokens?.idToken?.toString(),
                        AccessToken: session.tokens?.accessToken?.toString()
                    });
                    console.log('[DataChannel] Credentials sent to stream:', credentialsMessage);
                    this.dataChannelSendMessage(credentialsMessage);
                }).catch(error => {
                    console.error('Failed to get credentials for data channel:', error);
                });
                break;
            case "requestAWSCredentials":
                this.setState({showLoading: false });
                console.log("[DataChannel] Sending AWS Credentials to Stream.");
                Promise.all([this.getAWSCredentials(), fetchAuthSession()]).then(([awsCredentials, session]) => {
                    const awsCredentialsMessage = JSON.stringify({
                        action: "awsCredentialsResponse",
                        IdToken: session.tokens?.idToken?.toString(),
                        AccessToken: session.tokens?.accessToken?.toString(),
                        SessionToken: "", // Cognito session token not used in current auth flow
                        AWSCredentials: awsCredentials
                    });
                    console.log('[DataChannel] AWS Credentials sent to stream:', awsCredentialsMessage);
                    this.dataChannelSendMessage(awsCredentialsMessage);
                }).catch(error => {
                    console.error('Failed to get AWS credentials for data channel:', error);
                });
                break;
            case "refreshCredentials":
                console.log('[DataChannel] refreshCredentials received, sending current tokens');
                if (this.state.status === StreamState.RUNNING) {
                    Promise.all([this.getAWSCredentials(), fetchAuthSession()]).then(([awsCredentials, session]) => {
                        const refreshMessage = JSON.stringify({
                            action: "credentialsRefresh",
                            IdToken: session.tokens?.idToken?.toString(),
                            AWSCredentials: awsCredentials
                        });
                        console.log('[DataChannel] Current credentials sent to stream');
                        this.dataChannelSendMessage(refreshMessage);
                    }).catch(error => {
                        console.error('Failed to refresh credentials for data channel:', error);
                    });
                }
                break;
            case "requestLightFile":
                this.disableFullScreen();
                this.setState({ showUploadModal: true });
                break;
            default:
                console.log("[DataChannel] No action was taken for " + action);
                break;
        }
    }

    disableFullScreen() {
        const element = this.getVideoElement();
        if (element) {
            try {
                // Check if document is in fullscreen first
                if (!document.fullscreenElement) {
                    console.log("Document is not in fullscreen mode");
                    return;
                }
    
                // Define the document with browser-specific methods
                interface CustomDocument extends Document {
                    webkitExitFullscreen?: () => Promise<void>;
                    mozCancelFullScreen?: () => Promise<void>;
                    msExitFullscreen?: () => Promise<void>;
                    webkitFullscreenElement?: Element | null;
                    mozFullScreenElement?: Element | null;
                    msFullscreenElement?: Element | null;
                }
    
                const doc = document as CustomDocument;
    
                // Check for browser-specific fullscreen element
                const isInFullScreen = doc.fullscreenElement || 
                                     doc.webkitFullscreenElement || 
                                     doc.mozFullScreenElement ||
                                     doc.msFullscreenElement;
    
                if (isInFullScreen) {
                    // Wrap in Promise to handle async nature of exitFullscreen
                    const exitFullscreen = async () => {
                        try {
                            if (doc.exitFullscreen) {
                                await doc.exitFullscreen();
                            } else if (doc.webkitExitFullscreen) {
                                await doc.webkitExitFullscreen();
                            } else if (doc.mozCancelFullScreen) {
                                await doc.mozCancelFullScreen();
                            } else if (doc.msExitFullscreen) {
                                await doc.msExitFullscreen();
                            }
                        } catch (error) {
                            console.error('Error exiting fullscreen:', error);
                        }
                    };
    
                    exitFullscreen().then(() => {
                        this.gameLiftStreams?.attachInput();
                        this.setState({ inputEnabled: true });
                    });
                }
    
                // Handle keyboard lock with proper typing
                interface CustomNavigator extends Navigator {
                    keyboard?: {
                        lock: (keys: string[]) => Promise<void>;
                        unlock: () => void;
                    };
                }
    
                const customNavigator = navigator as CustomNavigator;
                if (customNavigator.keyboard) {
                    customNavigator.keyboard.unlock();
                }
            } catch (error) {
                console.error('Error in disableFullScreen:', error);
                // Ensure input is still attached even if fullscreen exit fails
                this.gameLiftStreams?.attachInput();
                this.setState({ inputEnabled: true });
            }
        }
    }    

    enableFullScreen() {
        const element = this.getVideoElement();
        if (element) {
            this.gameLiftStreams?.attachInput();
            this.setState({ inputEnabled: true });
    
            // Define element with browser-specific methods
            interface CustomHTMLElement extends HTMLElement {
                webkitRequestFullscreen?: () => Promise<void>;
                mozRequestFullScreen?: () => Promise<void>;
                msRequestFullscreen?: () => Promise<void>;
            }
    
            const customElement = element as CustomHTMLElement;
    
            if (customElement.requestFullscreen) {
                customElement.requestFullscreen();
            } else if (customElement.webkitRequestFullscreen) { /* Safari */
                customElement.webkitRequestFullscreen();
            } else if (customElement.msRequestFullscreen) { /* IE11 */
                customElement.msRequestFullscreen();
            } else if (customElement.mozRequestFullScreen) { /* Firefox */
                customElement.mozRequestFullScreen();
            }
    
            // Handle keyboard lock with proper typing
            interface CustomNavigator extends Navigator {
                keyboard?: {
                    lock: (keys: string[]) => Promise<void>;
                    unlock: () => void;
                };
            }
    
            const customNavigator = navigator as CustomNavigator;
            if (customNavigator.keyboard) {
                customNavigator.keyboard.lock(["Escape"]);
            }
        }
    }    

    closeConnection() {
        console.log("Closing connnection.");
        this.setState({ status: StreamState.STOPPED, micro: false, arn: "", inputEnabled: false, region: ""});
        this.gameLiftStreams?.close();
        this.createGameLiftStream();
        this.props.setActiveStream(null);
        this.props.refreshList();
        this.startPolling();
        this.clearLocalStorage();
    }

    onButtonDropdownItemClick(details: CustomEvent<ButtonDropdownProps.ItemClickDetails>) {
        switch (details.detail.id) {
            case "endstream":
                this.closeConnection();
                break;
            case "fullscreen":
                this.enableFullScreen();
                break;
            case "epl":
                this.enablePointerLock();
                break;
            case "input":
                this.enableInput();
                break;
            case "micro":
                this.enableMic();
                break;
            case "bugreport":
                this.createBugReport();
                break;

        }
    }

    enableInput() {
        if (!this.state.inputEnabled) {
            this.gameLiftStreams?.attachInput();
            this.setState({ inputEnabled: true })
        } else {
            this.gameLiftStreams?.detachInput();
            this.setState({ inputEnabled: false })
        }
    }

    private createBugReport()
    {
        this.gameLiftStreams?.detachInput();
        this.setState({ inputEnabled: false })
        this.setState({ showBugReportModal: true });
    }

    private handleBugReportSubmit = async (bugReport: string) => {
        this.setState({ isSubmittingBugReport: true });
        try {
            // First capture the video screenshot
            const videoElement = this.getVideoElement();
            if (!videoElement) {
                throw new Error('Video element not found');
            }
    
            // Create canvas and capture video frame
            const canvas = document.createElement('canvas');
            const context = canvas.getContext('2d');
            
            if (!context) {
                throw new Error('Could not get canvas context');
            }
    
            // Scale down to 80% of original size
            const scale = 0.8;
            canvas.width = videoElement.videoWidth * scale;
            canvas.height = videoElement.videoHeight * scale;
    
            // Enable better quality scaling
            context.imageSmoothingEnabled = true;
            context.imageSmoothingQuality = 'high';
    
            // Draw the scaled video frame
            context.drawImage(videoElement, 0, 0, canvas.width, canvas.height);
    
            // First submit the bug report and get the presigned URL
            const session = await fetchAuthSession();
            const response = await post({
                apiName: "gamecast-api",
                path: "/bugreport",
                options: {
                    headers: {
                        Authorization: `Bearer ${session.tokens?.idToken?.toString()}`
                    },
                    body: {
                        streamGroupId: this.props.sgId,
                        streamAppId: this.props.appId,
                        streamSessionId: this.state.arn,
                        userName: this.props.auth.user?.attributes?.email || this.props.auth.user?.username,
                        report: bugReport,
                        region: this.props.region,
                        timeStamp: new Date().toISOString(),
                    }
                }
            });
    
            // Parse the response
            const { body } = await response.response;
            const data = JSON.parse(await body.text());
            const uploadUrl = data.uploadUrl;
            // Convert canvas to Blob
            const blob = await new Promise<Blob>((resolve) => {
                canvas.toBlob((blob) => {
                    resolve(blob!);
                }, 'image/jpeg', 0.8); // 80% quality JPEG
            });
    
            // Upload the screenshot to S3 using the presigned URL
            const uploadResponse = await fetch(uploadUrl, {
                method: 'PUT',
                body: blob,
                headers: {
                    'Content-Type': 'image/jpeg'
                }
            });
    
            if (!uploadResponse.ok) {
                throw new Error('Failed to upload screenshot');
            }
    
            console.log('Bug report and screenshot submitted successfully');
            this.setState({ 
                showBugReportModal: false, 
                isSubmittingBugReport: false 
            });
            this.gameLiftStreams?.attachInput();
            this.setState({ inputEnabled: true });
            this.props.setSuccess('Bug report and screenshot submitted successfully');
        } catch (error) {
            console.error('Error submitting bug report:', error);
            this.setState({ 
                showBugReportModal: false, 
                isSubmittingBugReport: false 
            });
            this.props.setError('Failed to submit bug report');
            this.gameLiftStreams?.attachInput();
            this.setState({ inputEnabled: true });
        }
    };    

    async enableMic() {
        try {
            await this.gameLiftStreams!.enableMicrophone();
            this.setState({ micro: true });
        } catch (e) {
            console.log(e)
        }
    }

    enablePointerLock() {
        const videoElement = this.getVideoElement();
        if (videoElement && !this.state.pointerLocked) {
            videoElement.requestPointerLock();
            this.setState({ pointerLocked: true })
        } else {
            document.exitPointerLock();
            this.setState({ pointerLocked: false })
        }
    }

    private getButtonDropdownItems() {
        const { status, inputEnabled, /*micro*/ } = this.state;
        const isRunning = status === StreamState.RUNNING;
        //const isStopped = status === StreamState.STOPPED;

        return [
            {
                text: 'End Stream',
                id: 'endstream',
                disabled: !isRunning,
            },
            /*{
                text: 'Enable Pointer Lock',
                id: 'epl',
                disabled: !isRunning,
            },*/
            {
                text: inputEnabled ? 'Disable Input' : 'Enable Input',
                id: 'input',
                disabled: !isRunning,
            },
            {
                text: 'Report Bug',
                id: 'bugreport',
                disabled: !isRunning,
            },

        ];
    }

    private getStorageKey(): string {
        return `gamecast-stream-information`;
    }

    private saveStreamState(streamGroupIdentifier: string, streamSessionIdentifier: string, streamRegion: string) : void{
        console.log("Saving stream information");
        const streamState = {
            streamGroupIdentifier: streamGroupIdentifier,
            streamSessionIdentifier: streamSessionIdentifier,
            streamRegion: streamRegion,
            appId: this.props.appId
        };
        localStorage.setItem(this.getStorageKey(), JSON.stringify(streamState));
        console.log("Stream information saved: ", this.getStorageKey(), streamState);

        const savedData = localStorage.getItem(this.getStorageKey());
        console.log('Verified saved data:', savedData);
    }

    private async checkReconnect()
    {
        // Exit if stream is not in STOPPED state
        if (this.state.status !== StreamState.STOPPED) {
            return;
        }

        this.setState({checkingReconnect:true});
        const storageKey = this.getStorageKey();
        console.log('Loading with key:', storageKey);
        const savedState = localStorage.getItem(storageKey);
        console.log('Retrieved saved state:', savedState);
        if (savedState) {
            console.log("Stream information loaded!");
            const parsedState = JSON.parse(savedState);

            // Only check reconnect if this game owns the saved session (same stream group and app)
            if (parsedState.streamGroupIdentifier !== this.props.sgId ||
                parsedState.appId !== this.props.appId) {
                this.setState({checkingReconnect: false, canReconnect: false});
                return;
            }

            this.saveStreamState(parsedState.streamGroupIdentifier, parsedState.streamSessionIdentifier, parsedState.streamRegion);

            const sessionToken = await this.ensureValidToken();
            const getSession = get({
                apiName: "gamecast-api",
                path: `/session/${encodeURIComponent(parsedState.streamGroupIdentifier)}/${encodeURIComponent(parsedState.streamSessionIdentifier)}`,
                options: {
                    headers: {
                        Authorization: `Bearer ${sessionToken}`
                    }
                }
            });

            const getSessionResponse = await getSession.response;
            const sessionData = JSON.parse(await getSessionResponse.body.text());
            console.log("Session data response:", sessionData);

            if (sessionData.status === 'PENDING_CLIENT_RECONNECTION') {
                console.log('Stream status pending reconnect, can reconnect.');
                this.setState({checkingReconnect:false, canReconnect: true});
                this.props.onReconnectAvailable(this.props.sgId);
                return;
            }
        }
        console.log('Stream status NOT pending reconnect, can not reconnect.');
        this.setState({checkingReconnect:false, canReconnect: false});
        this.props.onReconnectAvailable(null);
        this.clearLocalStorage();
    }

    private loadStreamState(): void {
        const storageKey = this.getStorageKey();
        console.log('Loading with key:', storageKey);
        const savedState = localStorage.getItem(storageKey);
        console.log('Retrieved saved state:', savedState);
        if (savedState) {
            console.log("Stream information loaded!");
            const parsedState = JSON.parse(savedState);
            
            //attempt to reconnect
            this.saveStreamState(parsedState.streamGroupIdentifier, parsedState.streamSessionIdentifier, parsedState.streamRegion);
            this.reconnectStream(parsedState.streamGroupIdentifier, parsedState.streamSessionIdentifier, parsedState.streamRegion);
        }
        else{
            console.log("Stream information could not load");
        }
    }
    
    private clearLocalStorage(): void {
        this.setState({canReconnect: false});
        localStorage.removeItem(this.getStorageKey());
    }

    private handleWindowFocus = () => {
        // Only refocus if stream is running and input is enabled
        if (this.state.status === StreamState.RUNNING && this.state.inputEnabled) {
            const videoElement = this.getVideoElement();
            if (videoElement) {
                videoElement.focus();
            }
        }
    };






    render() {
        const isStopped = this.state.status === StreamState.STOPPED;
        const isCompact = isStopped && this.props.hasActiveStream && !this.props.isActiveStream;

        if (isCompact) {
            return null;
        }

        return<Container
            header={
                <Header
                    variant="h2"
                    actions={
                        <SpaceBetween direction="horizontal" size="m">
                            {isStopped && this.props.streamClass && (
                                <Badge color="grey">{this.props.streamClass}</Badge>
                            )}
                            {this.props.isRecommended && isStopped && (
                                <Badge color="blue">Recommended</Badge>
                            )}
                            {isStopped && (<Badge
                            color={
                                this.props.remainingStreams > 7 ? 'green' :
                                this.props.remainingStreams > 3 ? 'severity-low' :
                                this.props.remainingStreams > 0 ? 'severity-medium' : 'severity-high'
                            }
                            >
                                Remaining Streams: {this.props.remainingStreams}
                            </Badge>)}
                            {this.state.status === StreamState.RUNNING && (<Button
                                iconName="zoom-to-fit" variant="icon"
                                onClick={this.enableFullScreen}>
                            </Button>)}
                            {this.state.status !== StreamState.STOPPED && (<ButtonDropdown items={this.getButtonDropdownItems()}
                                onItemClick={this.onButtonDropdownItemClick}>
                                Options
                            </ButtonDropdown>)}
                        </SpaceBetween>
                    }
                    description={isStopped ? this.props.gameDescription : undefined}
                >
                    {`${this.props.gameName}`}
                </Header>
            }
            >
            <Box textAlign="center">
                <div style={{ position: 'relative',
                        height: isStopped ? 'auto' : 'calc(100vh - 200px)',
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                    {(isStopped && !this.state.checkingReconnect) ? (
                        <SpaceBetween direction="vertical" size="s" alignItems="center">
                            <span style={{
                                fontSize: '28px',
                                fontWeight: '300',
                                fontFamily: 'Amazon Ember, sans-serif'
                            }}>
                                {this.state.canReconnect
                                    ? `Reconnect to your ${this.props.gameName} stream`
                                    : `Start a stream to view ${this.props.gameName}`
                                }
                            </span>
                            <Button
                                onClick={() => {
                                    if (this.state.status === StreamState.RUNNING) {
                                        return this.closeConnection();
                                    }
                                    return this.state.canReconnect ? this.loadStreamState() : this.createStreamSession();
                                }}
                                variant="primary"
                                disabled={!this.state.canReconnect && this.props.remainingStreams <= 0}
                            >
                                {(this.state.canReconnect ? "Reconnect Stream" : "Start Stream")}
                            </Button>
                            {!this.state.canReconnect && this.props.remainingStreams <= 0 && (
                                <span style={{ color: '#d91515', fontSize: '14px' }}>
                                    No streams available. Try another option below.
                                </span>
                            )}
                        </SpaceBetween>
                    ) : null}
                    {this.state.showLoading === true ? (
                        <LoadingScreen message={this.state.loadingMessage} />
                    ) : null}
                    <>
                        <video
                            id={`StreamVideoElement${this.props.gameName}${this.props.sgId}`}
                            autoPlay
                            playsInline
                            style={{
                                width: this.state.status !== StreamState.STOPPED ? "100%" : "0%",
                                height: this.state.status !== StreamState.STOPPED ? "100%" : "0%",
                                objectFit: 'contain',
                                display: 'block'
                            }}
                        />
                        <audio
                            id={`StreamAudioElement${this.props.gameName}${this.props.sgId}`}
                            autoPlay
                            playsInline
                        />
                    </>
                </div>
            </Box>
            <UploadModal
                visible={this.state.showUploadModal}
                onDismiss={() => this.setState({ showUploadModal: false })}
                onUpload={this.handleJsonUpload}
            />
            <BugReportModal
                visible={this.state.showBugReportModal}
                onDismiss={() => this.setState({ showBugReportModal: false })}
                onSubmit={this.handleBugReportSubmit}
                isSubmitting={this.state.isSubmittingBugReport}
            />

        </Container>;
    }
}

export default Game;